import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { map } from 'rxjs/operators';
import { ClubOverviewService } from '~features/club-overview/club-overview.service';
import { ModalService } from '~shared/components/elements/global-modal/modal.service';
import { ButtonsTranslations } from '~shared/components/buttons/buttons-translations.service';
import { NotificationType } from '~core/enum';
import { FormLabelTranslationService } from '~shared/components/form-fields/translations/form-label-translations.service';
import { Club } from '~core/models';
import { ClubService, DefaultNotificationService, StorageService } from '~core/services';
import { ClubStatusTranslationsService, ClubTranslationsService } from '~shared/translations';


/**
 * Component that displays the workspace form for the gdpr settings
 */
@UntilDestroy()
@Component({
  selector: 'app-edit-club-gdpr-settings',
  templateUrl: './edit-club-gdpr-settings.component.html',
  styleUrls: ['../../../pages/club-settings/club.component.scss']
})
export class EditClubGdprSettingsComponent implements OnInit, OnDestroy {
  public clubForm: UntypedFormGroup;
  public spinnerName: 'editClubGdprSettings';
  @Input() public selectedClub: Club;
  public retentionPerdiodOptions: Array<any> = [];
  public i18nYears: string = $localize`:@@clubComponent.text.years:years`;
  public i18nModalTitle: string;
  public i18nModalParagraph: string;

  constructor(
    public clubService: ClubService,
    public clubOverviewService: ClubOverviewService,
    private notificationService: DefaultNotificationService,
    public flTranslations: FormLabelTranslationService,
    public buttonsTranslations: ButtonsTranslations,
    private clubTranslations: ClubTranslationsService,
    private clubStatuses: ClubStatusTranslationsService,
    private storage: StorageService,
    private formBuilder: UntypedFormBuilder,
    private modalService: ModalService
  ) { }

  public ngOnInit(): void {
    for (let i = 3; i <= 10; i++) {
      this.retentionPerdiodOptions.push({ value: i, status: `${i} ${this.i18nYears}` });
    }
    this.i18nModalTitle = $localize`:@@clubOverview.modalTitle.changeRetentionPeriod:Change retention period?`;
    this.i18nModalParagraph = $localize`:@@clubOverview.modalParagraph.deleteFanAttribute:This decision can't be reverted as affected invoices will be irreversibly removed. Would you like to proceed?`;
    this.loadData();
  }

  public ngOnDestroy(): void { }

  /**
   * Handles updating of the club-overview. If form is valid submit the form otherwise
   * displays errors.
   */
  public updateClub(): any {
    if (this.clubForm.valid) {
      this.clubService.updateClub(this.clubForm.getRawValue())
        .pipe(map(res => new Club(res, this.clubStatuses)), untilDestroyed(this))
        .subscribe({
          next: (res: Club) => {
            this.storage.updateSelectedClub(res);
            this.clubOverviewService.toggleAllModal(false);
            this.modalService.setStatus(false);
            this.notificationService.showNotification({
              type: NotificationType.SUCCESS,
              message: this.clubTranslations.i18nSuccessClubUpdate
            });
          },
          error: e => {
            this.modalService.setStatus(false);
            this.notificationService.showNotification({
              type: NotificationType.ALERT,
              message: this.clubTranslations.i18nErrorClubUpdate
            });
          }
        });
    }
  }

  /**
   * Fetch the club-overview data via the url parameter and fill the club-overview form.
   */
  public loadData(): void {
    this.clubForm = this.formBuilder.group({
      id: [{ value: this.selectedClub.id, disabled: true }],
      gdprSettingsOrdersRetentionPeriodInYears: [this.selectedClub.gdprSettingsOrdersRetentionPeriodInYears],
    });
  }

  public async showEditModal(): Promise<any> {
    this.modalService.setModalOptions(this.i18nModalTitle, this.i18nModalParagraph);
    const promise = await new Promise<boolean>((resolve, reject) => this.modalService.openModal(resolve, reject));
    if (promise) {
      this.updateClub();
    }
  }

}
